import * as v from 'valibot';


export const UUIDScheme = v.pipe(v.string(), v.uuid());

export type UUID = v.InferOutput<typeof UUIDScheme>;

export const UserIDSchame = v.pipe(v.string(), v.minLength(5));

export type UserID = v.InferOutput<typeof UserIDSchame>;

export const LangScheme = v.picklist(["cs", "en", "es"]);

export type Lang = v.InferOutput<typeof LangScheme>;


export const DateTimeScheme = v.pipe(v.string(), v.isoDateTime());

export type DateTime = v.InferOutput<typeof DateTimeScheme>;

export const DateDayScheme = v.pipe(v.string(), v.isoDate());

export type DateDay = v.InferOutput<typeof DateDayScheme>;

export const MailScheme = v.pipe(v.string(), v.email());

export type Mail = v.InferOutput<typeof MailScheme>;


export const FileScheme = v.object({
    id: v.optional(v.string()),
    type: v.literal("EASY_DB_FILE"),
    url: v.string(),
});

export const UserSchema = v.object({
    name: v.pipe(v.string(), v.minLength(2), v.maxLength(50)),
    color: v.string(),
    icon: v.pipe(v.string(), v.minLength(1)),
});

export type User = v.InferOutput<typeof UserSchema>;

export const UserLoginSchema = v.object({
    localId: UUIDScheme,
    mail: v.pipe(v.string(), v.maxLength(50), v.email()),
    password: v.pipe(v.string(), v.minLength(6), v.maxLength(50)),
});

export type UserLogin = v.InferOutput<typeof UserLoginSchema>;

export const UserSecretsSchema = v.object({
    userId: v.pipe(v.string(), v.minLength(5)),
    token: v.string(),
});

export type UserSecrets = v.InferOutput<typeof UserSecretsSchema>;


export const ThemeSchema = v.union([
    v.literal(""),
    v.literal("light"),
    v.literal("dark"),
    v.literal("parchment"),
    v.object({
        type: v.literal("custom"),
        // max 1024x1024
        background: FileScheme,
        // max 250x250
        backgroundPreview: FileScheme,
        backgroundOpacity: v.number(),
        mode: v.picklist(["light", "dark"]),
    })
]);

export type Theme = v.InferOutput<typeof ThemeSchema>;


// -------------------------------  Countdown  ---------------------------------

export const CountdownSchema = v.object({
    _id: v.string(),
    userId: UserIDSchame,
    changed: v.string(),
    lang: LangScheme,
    theme: ThemeSchema,
    countdown: DateTimeScheme,
    title: v.pipe(v.string(), v.minLength(1), v.maxLength(100)),
    description: v.pipe(v.string(), v.maxLength(500)),
    messageAfter: v.pipe(v.string(), v.maxLength(100)),
    where: v.pipe(v.string(), v.maxLength(50)),
    public: v.boolean(),
});

export type Countdown = v.InferOutput<typeof CountdownSchema>;


// ----------------------------------  Poll  -----------------------------------

export const PollVoteSchema = v.boolean();

export const PollOptionSchema = v.object({
    description: v.string(),
    users: v.record(v.string(), PollVoteSchema),
});

export const PollSchema = v.object({
    _id: v.string(),
    userId: UserIDSchame,
    changed: v.string(),
    lang: LangScheme,
    theme: ThemeSchema,
    title: v.pipe(v.string(), v.minLength(1), v.maxLength(100)),
    description: v.pipe(v.string(), v.maxLength(500)),
    voteCount: v.number(),
    users: v.record(v.string(), UserSchema),
    options: v.record(v.string(), PollOptionSchema),
    public: v.boolean(),
});

export type Poll = v.InferOutput<typeof PollSchema>;


// ----------------------------------  Who  ------------------------------------

export const VoteSchema = v.picklist(["yes", "maybe", "no"]);

export type Vote = v.InferOutput<typeof VoteSchema>;

export const VoteFullSchema = v.object({
    vote: VoteSchema,
    comment: v.pipe(v.string(), v.maxLength(400)),
});

export type VoteFull = v.InferOutput<typeof VoteFullSchema>;

export const WhoSchema = v.object({
    _id: v.string(),
    userId: UserIDSchame,
    changed: v.string(),
    lang: LangScheme,
    theme: ThemeSchema,
    title: v.pipe(v.string(), v.minLength(1), v.maxLength(100)),
    description: v.pipe(v.string(), v.maxLength(500)),
    where: v.pipe(v.string(), v.maxLength(50)),
    when: v.string(),
    users: v.record(v.string(), UserSchema),
    isMaybeOption: v.boolean(),
    votes: v.record(UserIDSchame, VoteFullSchema),
    public: v.boolean(),
});

export type Who = v.InferOutput<typeof WhoSchema>;


// ----------------------------------  When  -----------------------------------

export const WhenOptionSchema = v.object({
    description: v.string(),
    users: v.record(UserIDSchame, VoteFullSchema),
});

export type WhenOption = v.InferOutput<typeof WhenOptionSchema>;

export const WhenSchema = v.object({
    _id: v.string(),
    userId: UserIDSchame,
    changed: v.string(),
    lang: LangScheme,
    theme: ThemeSchema,
    title: v.pipe(v.string(), v.minLength(1), v.maxLength(100)),
    description: v.pipe(v.string(), v.maxLength(500)),
    where: v.pipe(v.string(), v.maxLength(50)),
    users: v.record(v.string(), UserSchema),
    isMaybeOption: v.boolean(),
    options: v.record(v.string(), WhenOptionSchema),
    public: v.boolean(),
});

export type When = v.InferOutput<typeof WhenSchema>;

// ----------------------------------  Team  -----------------------------------

export const TeamUserFlagSchema = v.object({
    isAdmin: v.boolean(),
    isMember: v.boolean(),
});

export const TeamEventSchema = v.object({
    // title: v.pipe(v.string(), v.minLength(1), v.maxLength(100)),
    description: v.pipe(v.string(), v.maxLength(500)),
    users: v.record(v.string(), VoteFullSchema),
});

export type TeamEvent = v.InferOutput<typeof TeamEventSchema>;

export const TeamSchema = v.object({
    _id: v.string(),
    changed: v.string(),
    lang: LangScheme,
    theme: ThemeSchema,
    title: v.pipe(v.string(), v.minLength(1), v.maxLength(100)),
    description: v.pipe(v.string(), v.maxLength(500)),
    users: v.record(v.string(), v.intersect([UserSchema, TeamUserFlagSchema])),
    events: v.record(v.string(), TeamEventSchema),
});

export type Team = v.InferOutput<typeof TeamSchema>;

export const TeamPreviewSchema = v.pick(TeamSchema, ["_id", "lang", "title", "description", "theme"]);

export type TeamPreview = v.InferOutput<typeof TeamPreviewSchema>;